import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["passwordField", "strengthCheckField", "checkFields", "width", "strengthId", "tooltipId"];
import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useField, useFormikContext } from 'formik';
import Worker from 'worker-loader!./zxcvbn-worker'; // eslint-disable-line

import * as Comlink from 'comlink';
import { VisuallyHidden } from '@figure/frontlib-accessibility';
import StyledContainer from '../../../Styled/StyledContainer';
import { accessPropertyByDotNotationString, toAlphanumericDashString } from '../../../../utils';
import { flex, hoverChild, TRANSITION_DEFAULT, highContrast } from '../../../../styling';
import { COLORS, MARGINS, TYPOGRAPHY, TYPOGRAPHY_CLASSNAMES } from '../../../../themes';
import { DynamicContentTooltip } from '../../../tooltip';
import { InfoI } from '../../../svg';
import { NO_TRANSITION_CLASSNAME } from '../../../svg/Svg';
import { usePrevious } from '../../../../hooks';
var READABLE_SCORES = ['very poor', 'poor', 'good', 'very good'];
var SCORE_COLORS = [COLORS.NEGATIVE_500, COLORS.ORANGE, COLORS.PRIMARY_500, COLORS.GREEN];
var HC_COLORS = ['LinkText', 'VisitedText', 'ActiveText', 'Highlight'];
var Bar = /*#__PURE__*/styled.div.withConfig({
  displayName: "StrengthChecker__Bar",
  componentId: "sc-1l0a4qs-0"
})(["margin-top:12px;width:calc(100% / 4 - 3px);height:4px;flex:0 0 auto;transition:background ", ";background:", ";outline:1px solid transparent;", ""], TRANSITION_DEFAULT, function (_ref) {
  var $strength = _ref.$strength,
      $index = _ref.$index;
  return $strength >= $index + 1 ? SCORE_COLORS[$strength - 1] : COLORS.NEUTRAL_200;
}, highContrast(css(["background:", ";"], function (_ref2) {
  var $strength = _ref2.$strength,
      $index = _ref2.$index;
  return $strength >= $index + 1 ? HC_COLORS[$strength - 1] : 'Canvas';
})));
var Tooltip = /*#__PURE__*/styled.div.withConfig({
  displayName: "StrengthChecker__Tooltip",
  componentId: "sc-1l0a4qs-1"
})(["position:absolute;top:2px;right:0;z-index:-1;background:", ";border-radius:", "px;padding:", "px 2rem ", "px;max-width:", "px;width:600px;box-shadow:0 3px 6px rgba(0,0,0,0.09);transition:opacity ", ",transform ", ";", " span{display:block;}"], COLORS.WHITE, MARGINS.XXS, MARGINS.XS, MARGINS.XS, function (_ref3) {
  var $width = _ref3.$width;
  return $width;
}, TRANSITION_DEFAULT, TRANSITION_DEFAULT, function (_ref4) {
  var $visible = _ref4.$visible;
  return $visible ? css(["opacity:1;"]) : css(["opacity:0;"]);
});
var NO_PASSWORD = 'Please enter your password.';
var worker = new Worker();
var zxcvbn = Comlink.wrap(worker);

var _ref7 = /*#__PURE__*/React.createElement(StyledContainer, null, /*#__PURE__*/React.createElement(InfoI, {
  width: "1.7rem"
}));

var StrengthCheck = function StrengthCheck(_ref5) {
  var passwordField = _ref5.passwordField,
      strengthCheckField = _ref5.strengthCheckField,
      _ref5$checkFields = _ref5.checkFields,
      checkFields = _ref5$checkFields === void 0 ? [] : _ref5$checkFields,
      width = _ref5.width,
      strengthId = _ref5.strengthId,
      tooltipId = _ref5.tooltipId,
      rest = _objectWithoutProperties(_ref5, _excluded);

  if (!strengthCheckField) throw new Error('StrengthCheck requires strengthCheckField');

  var _useFormikContext = useFormikContext(),
      values = _useFormikContext.values;

  var _useField = useField({
    name: strengthCheckField
  }),
      _useField2 = _slicedToArray(_useField, 3),
      setValue = _useField2[2].setValue;

  var _useState = useState(0),
      _useState2 = _slicedToArray(_useState, 2),
      strength = _useState2[0],
      setStrength = _useState2[1];

  var _useState3 = useState([NO_PASSWORD]),
      _useState4 = _slicedToArray(_useState3, 2),
      rules = _useState4[0],
      setRules = _useState4[1];

  var prevPassword = usePrevious(values.password);
  var zxcvbnResultCountRef = useRef(0);
  useEffect(function () {
    var password = accessPropertyByDotNotationString(values, passwordField);
    var checkValues = checkFields.reduce(function (fields, key) {
      var value = accessPropertyByDotNotationString(values, key);
      if (value) return [].concat(_toConsumableArray(fields), [value]);
      return fields;
    }, []);

    if (password) {
      if (password !== prevPassword) {
        zxcvbn.check(password, checkValues).then(function (_ref6) {
          var _ref6$result = _ref6.result,
              score = _ref6$result.score,
              _ref6$result$feedback = _ref6$result.feedback,
              suggestions = _ref6$result$feedback.suggestions,
              warning = _ref6$result$feedback.warning,
              checkCount = _ref6.checkCount;
          // prevent out of order result from async return
          if (checkCount < zxcvbnResultCountRef.current) return;
          zxcvbnResultCountRef.current = checkCount;
          setStrength(password && !score ? 1 : score);

          if (strength >= 3) {
            setRules(['Great password.']);
            return;
          }

          var rules = [];
          if (warning) rules.push(warning);
          setRules([].concat(rules, _toConsumableArray(suggestions)).map(function (rule) {
            if (rule.charAt(rule.length - 1) !== '.') return "".concat(rule, ".");
            return rule;
          }));
        });
      }
    } else {
      setStrength(0);
      setRules([NO_PASSWORD]);
    } // eslint-disable-next-line

  }, [values, prevPassword]);
  useEffect(function () {
    setValue(strength); // eslint-disable-next-line
  }, [strength]);
  return /*#__PURE__*/React.createElement(StyledContainer, rest, /*#__PURE__*/React.createElement(StyledContainer, {
    $css: flex('space-between', 'center')
  }, READABLE_SCORES.map(function (_, index) {
    return /*#__PURE__*/React.createElement(Bar, {
      key: index,
      $strength: strength,
      $index: index
    });
  }), /*#__PURE__*/React.createElement(VisuallyHidden, {
    id: strengthId,
    "aria-live": "polite"
  }, strength ? "Password strength ".concat(READABLE_SCORES[strength - 1]) : null)), /*#__PURE__*/React.createElement(StyledContainer, {
    $css: flex('flex-end', 'center')
  }, /*#__PURE__*/React.createElement(StyledContainer, {
    as: "p",
    $css: css(["user-select:none;", " color:", ";"], TYPOGRAPHY.S, COLORS.NEUTRAL_600)
  }, "Password strength"), /*#__PURE__*/React.createElement(DynamicContentTooltip, {
    $css: css(["", " width:3.2rem;height:3.2rem;svg{border-radius:50%;}"], hoverChild("circle:not(.".concat(NO_TRANSITION_CLASSNAME, ")"), 'fill', COLORS.PRIMARY_600, COLORS.PRIMARY_500)),
    icon: _ref7,
    textContent: rules.join(' '),
    id: tooltipId,
    "aria-label": "Click to view suggestions for a stronger password."
  }, function (_ref8) {
    var visible = _ref8.visible;
    return /*#__PURE__*/React.createElement(Tooltip, {
      className: "strength-checker-tooltip",
      $visible: visible,
      $width: width
    }, rules.map(function (rule, index) {
      return /*#__PURE__*/React.createElement("span", {
        className: TYPOGRAPHY_CLASSNAMES.S_SEMIBOLD,
        key: "".concat(toAlphanumericDashString(rule), "-").concat(index)
      }, rule);
    }));
  })));
};

export default StrengthCheck;